import React, { useState, useEffect } from 'react';
import { FaWhatsapp, FaFacebookF, FaLinkedinIn } from 'react-icons/fa';
import './Contact.css';
import db from '../firebase';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { Helmet } from 'react-helmet';

function Contact() {
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', message: '' });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validate = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = "Name is required.";
    else if (!/^[A-Za-z\s]+$/.test(formData.name)) newErrors.name = "Name must contain letters only.";

    if (!formData.email.trim()) newErrors.email = "Email is required.";
    else if (!/^\S+@\S+\.\S+$/.test(formData.email)) newErrors.email = "Invalid email format.";

    if (!formData.phone.trim()) newErrors.phone = "Phone number is required.";
    else if (!/^\d{10,15}$/.test(formData.phone)) newErrors.phone = "Phone must be 10-15 digits.";

    if (!formData.message.trim()) newErrors.message = "Message is required.";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setIsSubmitting(true);
    try {
      await addDoc(collection(db, "contacts"), { ...formData, timestamp: serverTimestamp() });
      alert('✅ Thank you for contacting SamSoftix!');
      setFormData({ name: '', email: '', phone: '', message: '' });
    } catch (error) {
      console.error("❌ Firestore Error: ", error);
      alert('❌ Submission failed. Try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    const sections = document.querySelectorAll('.fade-section');
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.style.opacity = 1;
          entry.target.style.transform = 'translateY(0)';
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.2 });
    sections.forEach(sec => observer.observe(sec));
  }, []);

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "SamSoftix",
    url: "https://www.samsoftix.com",
    logo: "https://www.samsoftix.com/logo.png",
    description: "SamSoftix is a professional software house and coding training institute in Sambrial. We provide IT training, coding classes, web & app development, and business tech consultation by industry professionals and expert developers.",
    contactPoint: {
      "@type": "ContactPoint",
      telephone: "+92-3320472184",
      contactType: "Customer Service",
      areaServed: "PK",
      availableLanguage: ["English", "Urdu"]
    },
    sameAs: [
      "https://www.facebook.com/samsoftix",
      "https://www.linkedin.com/company/samsoftix",
      "https://wa.me/923320472184"
    ]
  };

  return (
    <main className="contact-page">
      <Helmet>
        <title>Contact SamSoftix | Coding Tutor & Software House Sambrial</title>
        <meta name="description" content="Contact SamSoftix in Sambrial for coding classes, IT training, software development, and professional developer training. Learn from industry experts or hire a top software house nearby." />
        <meta name="keywords" content="coding tutor Sambrial, software house Sambrial, coding classes Sambrial, IT training Sambrial, professional developer training, software development Sambrial, web development, app development, business tech solutions" />
        <link rel="canonical" href="https://www.samsoftix.com/contact" />

        <meta property="og:title" content="Contact SamSoftix | Coding Tutor & Software House Sambrial" />
        <meta property="og:description" content="Reach out to SamSoftix for coding classes by professional developers, software development, IT services, and consulting in Sambrial and nearby areas." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.samsoftix.com/contact" />
        <meta property="og:site_name" content="SamSoftix" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Contact SamSoftix | Coding Tutor & Software Services" />
        <meta name="twitter:description" content="Get in touch with SamSoftix for coding training by industry professionals, software development, IT services, and consulting in Sambrial and nearby areas." />

        <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
      </Helmet>

      <header className="contact-header fade-section" style={{ opacity: 0, transform: 'translateY(20px)' }}>
        <h1>Contact SamSoftix Expert Coding Tutors & Professional Software House in Sambrial</h1>
        <p>
          Connect with <strong>industry professionals and experienced developers</strong> for coding training, or partner with a <strong>trusted software house</strong> for your business solutions.
          We serve <strong>students, startups, and enterprise clients</strong> in Sambrial, nearby areas, and across the globe, delivering <strong>high-quality web, mobile, game, and AI solutions</strong> with reliability, innovation, and professionalism.
        </p>

      </header>

      <section className="contact-details fade-section" style={{ opacity: 0, transform: 'translateY(20px)' }}>
        <article className="detail-box">
          <h2>Email</h2>
          <p>info@samsoftix.com</p>
        </article>
        <article className="detail-box">
          <h2>Phone</h2>
          <p>+92 3320472184</p>
        </article>
        <article className="detail-box">
          <h2>Address</h2>
          <p>Sambrial, Pakistan<br />Serving Students & Clients Worldwide (Remote)</p>
        </article>
      </section>

      <section className="social-links fade-section" style={{ opacity: 0, transform: 'translateY(20px)' }}>
        <h2>Connect With Us on Social Media</h2>
        <a href="https://wa.me/923320472184" target="_blank" rel="noopener noreferrer" className="social-icon-text">
          <FaWhatsapp aria-label="Whatsapp" /><span>+92 3320472184</span>
        </a>
        <a href="https://www.facebook.com/samsoftix" target="_blank" rel="noopener noreferrer" className="social-icon-text">
          <FaFacebookF aria-label="Facebook" /><span>Facebook: SamSoftix</span>
        </a>
        <a href="https://www.linkedin.com/company/samsoftix" target="_blank" rel="noopener noreferrer" className="social-icon-text">
          <FaLinkedinIn aria-label="LinkedIn" /><span>LinkedIn: SamSoftix</span>
        </a>
      </section>

      <section className="contact-form fade-section" style={{ opacity: 0, transform: 'translateY(20px)' }}>
        <h2>Send Us a Message</h2>
        <p>
          Are you looking for <strong>coding classes by experienced industry developers</strong> or a <strong>trusted software house in Sambrial</strong>?
          Fill out the form below, and our experts will respond promptly to assist with <strong>web, mobile, game, and AI development</strong>, <strong>IT solutions</strong>, coding training, or business consultation.
          We proudly serve <strong>students, startups, and businesses locally and internationally</strong>.
        </p>

        <form onSubmit={handleSubmit} noValidate>
          <label htmlFor="name">Name</label>
          <input id="name" type="text" name="name" placeholder="Your Name" value={formData.name} onChange={handleChange} />
          {errors.name && <span className="error">{errors.name}</span>}

          <label htmlFor="email">Email</label>
          <input id="email" type="email" name="email" placeholder="Your Email" value={formData.email} onChange={handleChange} />
          {errors.email && <span className="error">{errors.email}</span>}

          <label htmlFor="phone">Phone</label>
          <input id="phone" type="text" name="phone" placeholder="Your Phone Number" value={formData.phone} onChange={handleChange} />
          {errors.phone && <span className="error">{errors.phone}</span>}

          <label htmlFor="message">Message</label>
          <textarea id="message" name="message" placeholder="Your Message" value={formData.message} onChange={handleChange} rows="6" />
          {errors.message && <span className="error">{errors.message}</span>}

          <button type="submit" disabled={isSubmitting}>
            {isSubmitting ? 'Sending...' : 'Send Message'}
          </button>
        </form>
      </section>
    </main>
  );
}

export default Contact;
